/**
 * /dashboard/posh/policy — POSH policy list (HR-admin only: generate from
 * a template, edit drafts, publish). Anyone else sees a pointer to
 * POSH → Policy & Committee, where every employee reads the policy.
 *
 * Migration note (Phase 3f — 2026-06-01): /dashboard/posh/policy/$id
 * is ported; <a href> swapped for typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { usePolicies } from "@/hooks/use-posh";
import { fmtDate } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PolicyGenerateForm } from "@/components/posh/policy-generate-form";
import { PublicIntakeCard } from "@/components/posh/public-intake-card";
import { PoshHrOnlyNotice } from "@/components/posh/posh-hr-only-notice";
import { policyErrorText } from "@/components/posh/posh-policy-errors";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/policy/")({
  component: PolicyRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function PolicyList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const q = usePolicies();
  if (q.isLoading) return <ListSkeleton />;
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load policies</AlertTitle>
        <AlertDescription>
          {policyErrorText(toApiError(q.error))}
        </AlertDescription>
      </Alert>
    );
  }
  const policies = q.data?.policies ?? [];
  if (policies.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No POSH policy yet. Generate one from a platform template to
          get started.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {policies.map((p) => (
        <Link
          key={p.id}
          to="/app/$tenantSlug/posh/policy/$id"
          params={{tenantSlug, id: String(p.id)}}
          className="group block"
        >
          <Card className="transition-colors group-hover:border-primary/40">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">{p.title}</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Version {p.version} · Updated{" "}
                    {fmtDate(p.updated_at)}
                    {p.published_at
                      ? ` · Published ${fmtDate(p.published_at)}`
                      : ""}
                  </p>
                </div>
                <StatusBadge status={p.status} />
              </div>
            </CardHeader>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function PolicyRoute() {
  // HR-only screen — the same check as the POSH tab strip. Anyone else is
  // pointed to Policy & Committee (the list query is never sent).
  const { user } = useAuth();
  const isAdmin = isHRAdmin(user?.role);
  if (!isAdmin) {
    return (
      <PoshModuleShell title="Policy">
        <PoshFeatureGate>
          <PoshHrOnlyNotice />
        </PoshFeatureGate>
      </PoshModuleShell>
    );
  }
  return (
    <PoshModuleShell
      title="Policy"
      description="Your organisation’s POSH policy, generated from a template, edited as a draft, then published as an immutable version."
      actions={<PolicyGenerateForm />}
    >
      <PoshFeatureGate>
        {/* Public / anonymous intake configuration — HR-admin only (the
            whole screen is; the backend RBAC re-checks every call). */}
        <div className="mb-6">
          <PublicIntakeCard />
        </div>
        <PolicyList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
